/**
 * Agent Inbox `HumanInterrupt` schema.
 *
 * Adopted wholesale from
 * https://github.com/langchain-ai/agent-inbox so any inbox-style consumer
 * (theirs or one we build later) can render our interrupts generically.
 *
 * The producer side: LangGraph rules call
 * `interrupt({ action_request, config, description })` to pause the
 * graph and surface a card to the user. The consumer side (Rails inbox
 * UI, Agent Inbox app, or curl) sends back a `HumanResponse` whose
 * `type` field tells the rule what the user chose.
 *
 * Lives in `@launch10/use-langgraph-stream` so both Rails (consumer) and
 * langgraph_app (producer) reference one source of truth.
 */

import type { BaseMessage } from "@langchain/core/messages";
import type { Thread, ThreadStatus } from "@langchain/langgraph-sdk";

/**
 * Configuration for a human interrupt, specifying what actions
 * are allowed when handling the interrupt.
 */
export interface HumanInterruptConfig {
  allow_ignore: boolean;
  allow_respond: boolean;
  allow_edit: boolean;
  allow_accept: boolean;
}

/**
 * Action request from the agent to the human.
 * This is part of a HumanInterrupt.
 */
export interface ActionRequest {
  action: string;
  args: Record<string, any>;
}

/**
 * Represents a human interrupt in the agent flow.
 * Similar to the LangGraph Interrupt type but with specific fields
 * for human interaction.
 *
 * `action` (on `action_request`) is internal dispatch metadata — it tells
 * the inbox UI which renderer to pick, not what to display to the user.
 * `title` and `description` are the human-facing strings the UI renders
 * verbatim. Producers should set both; consumers can fall back to deriving
 * a title from `action` if `title` is absent (legacy Agent Inbox payloads).
 */
export interface HumanInterrupt {
  action_request: ActionRequest;
  config: HumanInterruptConfig;
  /** Human-readable heading for the inbox card (e.g. "Update shared field?"). */
  title?: string;
  /** Human-readable body prose under the heading. */
  description?: string;
}

/**
 * Human response to an agent interrupt.
 * Can be one of several types: accept, ignore, provide a response, or edit.
 */
export type HumanResponse = {
  type: "accept" | "ignore" | "response" | "edit";
  args: null | string | ActionRequest;
};

/**
 * Extended human response type with additional properties for
 * tracking edit status.
 */
export type HumanResponseWithEdits = HumanResponse &
  (
    | { acceptAllowed?: false; editsMade?: never }
    | { acceptAllowed?: true; editsMade?: boolean }
  );

/**
 * Email data structure used in the thread values.
 */
export type Email = {
  id: string;
  thread_id: string;
  from_email: string;
  to_email: string;
  subject: string;
  page_content: string;
  send_time: string | undefined;
  read?: boolean;
  status?: "in-queue" | "processing" | "hitl" | "done";
};

/**
 * Main thread values type that contains the data
 * passed through the LangGraph workflow.
 */
export interface ThreadValues {
  email: Email;
  messages: BaseMessage[];
  triage: {
    logic: string;
    response: string;
  };
}

/**
 * Extended thread status type that includes our custom statuses.
 * Based on LangGraph ThreadStatus with additions.
 */
export type EnhancedThreadStatus = ThreadStatus | "human_response_needed";

/**
 * Base thread data interface with common properties.
 * This serves as the foundation for all thread data types.
 */
interface BaseThreadData<T extends Record<string, any> = Record<string, any>> {
  thread: Thread<T>;
  invalidSchema?: boolean;
}

/**
 * Thread data for non-interrupted states.
 * Follows the discriminated union pattern where the status
 * field acts as the discriminator.
 */
export interface GenericThreadData<
  T extends Record<string, any> = Record<string, any>,
> extends BaseThreadData<T> {
  status: "idle" | "busy" | "error";
  interrupts?: undefined;
}

/**
 * Thread data for interrupted state.
 * Contains additional fields specific to interruptions.
 */
export interface InterruptedThreadData<
  T extends Record<string, any> = Record<string, any>,
> extends BaseThreadData<T> {
  status: "interrupted";
  interrupts?: HumanInterrupt[];
}

/**
 * Thread data for human_response_needed state.
 */
export interface HumanResponseNeededThreadData<
  T extends Record<string, any> = Record<string, any>,
> extends BaseThreadData<T> {
  status: "human_response_needed";
  interrupts?: HumanInterrupt[];
}

/**
 * Union type for all thread data types.
 * Using discriminated union pattern for better type safety.
 */
export type ThreadData<T extends Record<string, any> = Record<string, any>> =
  | GenericThreadData<T>
  | InterruptedThreadData<T>
  | HumanResponseNeededThreadData<T>;

/**
 * Thread status with special "all" option for filtering.
 */
export type ThreadStatusWithAll = EnhancedThreadStatus | "all";

/**
 * Type of submission for human responses.
 */
export type SubmitType = "accept" | "response" | "edit";

// ─── Inbox action registry ────────────────────────────────────────────────
// `ActionRequest.action` is a free-form string in Agent Inbox's wire format.
// We constrain it to a narrow registry so the inbox UI can dispatch on
// action type and we don't grow per-guard string sprawl. Add new entries
// here ONLY when a concrete UI need exists; default to APPROVE_TOOL_CALL
// with a richer `args` bag for new approval kinds.
//
// Convention:
//   - action = the KIND of decision the user is making
//   - args   = domain-specific context (tool name, target id, etc.)
//   - description = human-readable prose surfacing to the inbox card

export const InboxActions = {
  /**
   * Agent wants to invoke a tool; user approves / rejects / responds.
   * `args` should at minimum carry `{ toolName: string }` plus whatever
   * else the inbox card needs to render (field names, target sites, etc.).
   */
  APPROVE_TOOL_CALL: "approve_tool_call",
  /**
   * Agent is asking the user a question and pausing until they answer.
   * `args` carries an `AskUserQuestionArgs` (the question prose + an ordered
   * list of option labels). The UI renders the labels as buttons in order
   * (+ an optional free-text "Other") and resumes with an
   * `AskUserQuestionResponse`. A dumb affordance: the tool just relays the
   * chosen text back to the agent — it carries no type, no metadata, and
   * mutates no state. The agent decides what to do with the answer.
   */
  ASK_USER_QUESTION: "ask_user_question",
} as const;
export type InboxAction = (typeof InboxActions)[keyof typeof InboxActions];

// ─── ask_user_question payloads ──────────────────────────────────────────
// Shared shape for the `ask_user_question` action so the producer (the
// `askUserQuestionTool` in langgraph_app) and the consumer (the card in this
// package, plus backend round-trip tests) agree on one wire format.

/** A single selectable choice. `label` is required; the rest is presentation. */
export interface AskUserQuestionOption {
  /** The choice text. */
  label: string;
  /** Optional one-line clarifier shown under the label. */
  description?: string;
  /** Optional semantic icon name (see the card's icon map, e.g. "product"). */
  icon?: string;
}

/** `action_request.args` for an `ask_user_question` interrupt. */
export interface AskUserQuestionArgs {
  /** The prose shown to the user. */
  question: string;
  /** Optional one-line context under the question. */
  subtitle?: string;
  /**
   * The choices, rendered as numbered rows in order. Canonically
   * `AskUserQuestionOption` objects, but a bare string label is accepted too
   * (and normalized at render) so a producer/consumer version skew never
   * crashes the card.
   */
  options: Array<string | AskUserQuestionOption>;
  /**
   * Checkboxes vs radios. Defaults to TRUE (absent = multi-select): the user
   * decides how many options apply and can add a free-text answer alongside
   * them; the resume carries `indexes` (and optionally `text`). Set false only
   * for truly exclusive choices — the card then renders radios and resumes
   * with a single `index` (or `text`).
   */
  multiSelect?: boolean;
}

/**
 * The resume payload for an `ask_user_question` interrupt.
 *
 * Single-select: either the user clicked an option (`index`, positional into
 * `options`) or typed a free-text answer (`text`). Never both.
 *
 * Multi-select: `indexes` carries every checked option (positional, ascending),
 * and `text` may ride along with it — the user's own answer aggregates with the
 * checked options rather than replacing them.
 */
export type AskUserQuestionResponse = {
  index?: number;
  indexes?: number[];
  text?: string;
};

/**
 * Build the resume payload for an `ask_user_question` interrupt. The single
 * source of truth used by the card (on click / on submit) AND by backend
 * round-trip tests, so both ends agree on the exact shape that flows through
 * `submit(null, { command: { resume } })`.
 */
export function buildAskUserQuestionResume(
  choice: { index: number } | { text: string } | { indexes: number[]; text?: string }
): AskUserQuestionResponse {
  if ("indexes" in choice) {
    const text = choice.text?.trim();
    // Free-text-only multi submit degrades to the single-select shape — the
    // consumer never needs to distinguish "typed under checkboxes" from
    // "typed under radios" when nothing is checked.
    if (choice.indexes.length === 0) return { text: text ?? "" };
    return text ? { indexes: choice.indexes, text } : { indexes: choice.indexes };
  }
  return "index" in choice ? { index: choice.index } : { text: choice.text };
}

// ─── Our semantic vocabulary (agentic-conversational) ────────────────────────
// Agent Inbox's `HumanResponse.type` is `accept | ignore | response | edit`.
// Their `ignore` is overloaded: it means "reject this action; agent runs one
// step so it sees the rejection." That collapse makes sense for their async
// dispatch context (queue of cards) but blurs two things in our mid-conversation
// context:
//
//   - "Reject this action" — agent KEEPS going, just doesn't apply this edit
//   - "Ignore the graph"   — terminate the thread without telling the agent
//                            (their `resolve` button; bypasses Command({resume})
//                            and hits a thread-status API instead)
//
// We need the first; we don't have a use case for the second yet. Below is the
// type our application code uses — semantic-clean names — and an adapter that
// converts to/from the wire format. Keep the wire format identical to
// Agent Inbox so any inbox-style consumer (theirs, ours, third-party) can
// render and respond to our interrupts without translation.
//
// If we ever need true "Ignore the graph" (abandon the conversation), that's
// a Rails-side thread-status action, not a resume payload. Different surface.

/**
 * The user's decision on a HumanInterrupt, in our application's vocabulary.
 *
 *   - "approve"  → run the agent's proposed action
 *   - "reject"   → don't run it; agent continues the conversation otherwise
 *   - "respond"  → don't run it; here's free-text feedback for the agent
 *
 * "edit" (modify args directly) and "ignore-the-graph" (terminate thread)
 * are intentionally NOT here. Add them only when a concrete user need exists.
 */
export type AgenticDecision =
  | { decision: "approve"; args: null }
  | { decision: "reject"; args: null }
  | { decision: "respond"; args: string };

/**
 * Translate our semantic decision into the Agent Inbox wire format.
 * Used at the boundary where we send the resume payload to LangGraph
 * (or where the inbox UI emits the response back to us).
 */
export function toHumanResponse(d: AgenticDecision): HumanResponse {
  switch (d.decision) {
    case "approve":
      return { type: "accept", args: null };
    case "reject":
      return { type: "ignore", args: null };
    case "respond":
      return { type: "response", args: d.args };
  }
}

/**
 * Translate an Agent Inbox wire response into our semantic decision.
 * Returns null for `edit` (we don't surface that affordance — see comment
 * above). Returns null for any unknown type so callers can fall back to
 * "treat as reject" behavior.
 */
export function fromHumanResponse(r: HumanResponse | undefined | null): AgenticDecision | null {
  if (!r) return null;
  switch (r.type) {
    case "accept":
      return { decision: "approve", args: null };
    case "ignore":
      return { decision: "reject", args: null };
    case "response":
      return { decision: "respond", args: typeof r.args === "string" ? r.args : "" };
    case "edit":
      return null; // not surfaced in our UI; treat as no-decision
    default:
      return null;
  }
}

/**
 * Configuration for an agent inbox.
 */
export interface AgentInbox {
  /**
   * A unique identifier for the inbox.
   */
  id: string;
  /**
   * The ID of the graph.
   */
  graphId: string;
  /**
   * The URL of the deployment. Either a localhost URL, or a deployment URL.
   */
  deploymentUrl: string;
  /**
   * Optional name for the inbox, used in the UI to label the inbox.
   */
  name?: string;
  /**
   * Whether or not the inbox is selected.
   */
  selected: boolean;
  /**
   * The tenant ID for the deployment (only for deployed graphs).
   */
  tenantId?: string;
  createdAt: string;
}
